import React, { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { emptyObject } from 'ui-components/default-props/empty';

import { useModal } from '../../../hooks/use-modal';
import { useModifiableSellerSetting } from '../../../hooks/use-modifiable-seller-setting';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { BASE_SCAN_PATH, HOME_PAGE_PATH } from '../../../routes/constants';
import Global from '../../../store/global-selectors';
import { bigCommerceAffiliateCode, completedStepsSvgs, OnboardingSteps } from '../constants';

const isStepComplete = (step, isOrderImportComplete, isShipFromComplete, isShippingServiceComplete, isCheckoutRatesComplete) => {
  switch (step) {
    case OnboardingSteps.OrderImport:
      return isOrderImportComplete;
    case OnboardingSteps.ShippingServices:
      return isShippingServiceComplete;
    case OnboardingSteps.ShipFrom:
      return isShipFromComplete;
    case OnboardingSteps.CheckoutRates:
      return isCheckoutRatesComplete;
    default:
      return false;
  }
};

export const useOnboardingComplete = (
  isPopup,
  shouldShowCheckoutRatesCard,
  isOrderImportComplete,
  isShipFromComplete,
  isShippingServiceComplete,
  isCheckoutRatesComplete,
) => {
  const isOnScanPage = useRouteMatch(BASE_SCAN_PATH);
  const isOnHomePage = useRouteMatch(HOME_PAGE_PATH);

  const affiliateCode = useSelector(Global.selectSellerAffiliateCode);

  const { isOpen: isPopupGuideOpen, open: openPopupGuide, close: closePopupGuide } = useModal();
  const [onboardingJson, setOnboardingJson] = useModifiableSellerSetting(SellerSettings.sellerOnboardingJson, emptyObject);
  const { hasPrintedLabels = false, isOnboardingDismissed = false } = onboardingJson;

  const { totalSteps, onboardingSteps, totalCompletedSteps, isOnboardingComplete, markOnboardingComplete } = useMemo(() => {
    const isBigCommerce = affiliateCode === bigCommerceAffiliateCode;
    // We should display a completed onboarding state if the user either completes all the onboarding steps, or has previously completed onboarding
    const _onboardingSteps = [OnboardingSteps.OrderImport, OnboardingSteps.ShippingServices, OnboardingSteps.ShipFrom];
    if (isBigCommerce && shouldShowCheckoutRatesCard && isPopup) {
      _onboardingSteps.push(OnboardingSteps.CheckoutRates);
    }
    const _totalSteps = _onboardingSteps.length;

    const _totalCompletedSteps = _onboardingSteps
      .map((step) => isStepComplete(step, isOrderImportComplete, isShipFromComplete, isShippingServiceComplete, isCheckoutRatesComplete))
      .filter(Boolean).length;

    return {
      totalSteps: _totalSteps,
      onboardingSteps: _onboardingSteps,
      totalCompletedSteps: _totalCompletedSteps,
      isOnboardingComplete: _totalCompletedSteps === _totalSteps,
      markOnboardingComplete: () =>
        setOnboardingJson((prevValue) => ({
          ...prevValue,
          isOnboardingComplete: true,
        })),
    };
  }, [
    affiliateCode,
    isCheckoutRatesComplete,
    isOrderImportComplete,
    isPopup,
    isShipFromComplete,
    isShippingServiceComplete,
    setOnboardingJson,
    shouldShowCheckoutRatesCard,
  ]);

  // Listen for changes to isOnboardingComplete and mark onboarding as complete in onboardingJson
  const isOnboardingCompleteRef = React.useRef(isOnboardingComplete);
  const hasPrintedLabelsRef = React.useRef(hasPrintedLabels);
  useEffect(() => {
    const wasOnboardingJustCompleted = !isOnboardingCompleteRef.current && isOnboardingComplete;
    const wasTheFirstLabelJustPrinted = !hasPrintedLabelsRef.current && hasPrintedLabels;
    if (!(isPopup && (wasOnboardingJustCompleted || wasTheFirstLabelJustPrinted))) {
      return;
    }
    if (wasOnboardingJustCompleted) {
      isOnboardingCompleteRef.current = true;
      markOnboardingComplete();
    }
    if (wasTheFirstLabelJustPrinted) {
      hasPrintedLabelsRef.current = hasPrintedLabels;
    }
    // Do not open the popup if the user is on the scan page
    // Do not open the popup if the user hasn't printed a label on pages that aren't the home page
    if (!isOnScanPage && (wasTheFirstLabelJustPrinted || isOnHomePage)) {
      openPopupGuide();
    }
  }, [hasPrintedLabels, isOnHomePage, isOnScanPage, isOnboardingComplete, isPopup, markOnboardingComplete, openPopupGuide]);

  return useMemo(() => {
    const togglePopup = () => {
      if (isPopupGuideOpen) {
        closePopupGuide();
      } else {
        openPopupGuide();
      }
    };
    return {
      isOnboardingComplete,
      totalSteps,
      onboardingSteps,
      totalCompletedSteps,
      svgToRender: completedStepsSvgs[`${totalCompletedSteps}/${totalSteps}`],
      isPopupGuideOpen,
      closePopupGuide,
      togglePopup,
      isOnboardingDismissed,
      dismissOnboarding: () =>
        setOnboardingJson((prevValue) => ({
          ...prevValue,
          isOnboardingDismissed: true,
        })),
      hasPrintedLabels,
    };
  }, [
    closePopupGuide,
    hasPrintedLabels,
    isOnboardingComplete,
    isOnboardingDismissed,
    isPopupGuideOpen,
    onboardingSteps,
    openPopupGuide,
    setOnboardingJson,
    totalCompletedSteps,
    totalSteps,
  ]);
};
